import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { ErrorState } from '@/modules/common/components/organisms/error-state';

import { TimeOffBalancesCompact } from '../components/balances/time-off-balances-compact';
import { TimeOffBalancesSection } from '../components/balances/time-off-balances-section';
import { TimeOffBalancesSkeleton } from '../components/balances/time-off-balances-skeleton';
import { TimeOffBalance } from '../types/time-off-balance';
import { useMemberTimeOffBalances } from './use-member-time-off-balances';

interface MemberTimeOffBalancesRenditions {
  expandedHeader: ReactNode;
  expandedBody: ReactNode;
  collapsed: ReactNode;
}

interface UseMemberTimeOffBalancesRenditionsParams {
  memberId: string | undefined;
  /**
   * When provided, each balance card renders a pencil that calls this with the
   * balance. Omitted in read mode (no manage permission) so no pencil shows.
   */
  onEditBalance?: (balance: TimeOffBalance) => void;
}

/**
 * Member-scoped twin of `useTimeOffBalancesRenditions`. Builds the expanded +
 * compact renditions of a member's balances for the collapsible header.
 *
 * Unsupported time-off types (e.g. Public Holiday) are filtered out at the data
 * hook level in `useMemberTimeOffBalances`, so this hook renders whatever it
 * receives. Optional `onEditBalance` threads a pencil into each expanded card.
 */
export const useMemberTimeOffBalancesRenditions = ({
  memberId,
  onEditBalance,
}: UseMemberTimeOffBalancesRenditionsParams): MemberTimeOffBalancesRenditions => {
  const { t } = useTranslation('authenticated.timeOff');
  const { data, isLoading, error, refetch, isFetching } = useMemberTimeOffBalances(memberId);

  const balances = data ?? [];
  const hasBalances = !isLoading && !error && balances.length > 0;

  const renderExpandedBody = () => {
    if (isLoading) return <TimeOffBalancesSkeleton />;
    if (error) {
      return (
        <ErrorState
          className="min-h-[180px]"
          title={t('errors.loadBalancesTitle')}
          error={error}
          action={{ label: t('errors.retry'), onClick: () => refetch(), isLoading: isFetching }}
        />
      );
    }
    if (balances.length === 0) return null;
    return <TimeOffBalancesSection balances={balances} onEditBalance={onEditBalance} />;
  };

  const expandedHeader = (
    <div className="flex flex-col gap-1">
      <h2 className="text-lg font-semibold">{t('member.balances.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('member.balances.description')}</p>
    </div>
  );

  return {
    expandedHeader,
    expandedBody: renderExpandedBody(),
    collapsed: hasBalances ? (
      <TimeOffBalancesCompact balances={balances} title={t('member.balances.compactTitle')} />
    ) : null,
  };
};
